import { Injectable, inject } from '@angular/core';
import { LoggerService } from '../logging/logger.service';

@Injectable({
  providedIn: 'root',
})
export class StorageService {
  private readonly logger = inject(LoggerService);

  /**
   * Sets a value in local storage.
   * @param key The key under which the value will be stored.
   * @param value The value to store.
   */
  setLocal(key: string, value: string): void {
    this.logger.debug('StorageService', 'Setting local storage value', { key, valueLength: value.length });
    try {
      localStorage.setItem(key, value);
    } catch (error) {
      this.logger.error('StorageService', 'Failed to set local storage value', error);
      throw error;
    }
  }
  
  /**
   * Gets a value from local storage.
   * @param key  The key of the value to retrieve.
   * @returns The value associated with the key, or null if the key does not exist.
   */
  getLocal(key: string): string | null {
    this.logger.debug('StorageService', 'Getting local storage value', { key });
    try {
      return localStorage.getItem(key);
    } catch (error) {
      this.logger.error('StorageService', 'Failed to get local storage value', error);
      return null;
    }
  }
  
  /**
   * Removes a value from local storage.
   * @param key The key of the value to remove.
   */
  removeLocal(key: string): void {
    this.logger.debug('StorageService', 'Removing local storage value', { key });
    try {
      localStorage.removeItem(key);
    } catch (error) {
      this.logger.error('StorageService', 'Failed to remove local storage value', error);
      throw error;
    }
  }
  
  /**
   * Sets a value in session storage.
   * @param key The key under which the value will be stored.
   * @param value The value to store.
   */
  setSession(key: string, value: string): void {
    this.logger.debug('StorageService', 'Setting session storage value', { key, valueLength: value.length });
    try {
      sessionStorage.setItem(key, value);
    } catch (error) {
      this.logger.error('StorageService', 'Failed to set session storage value', error);
      throw error;
    }
  }
  
  /**
   * Gets a value from session storage.
   * @param key The key of the value to retrieve.
   * @returns The value associated with the key, or null if the key does not exist.
   */
  getSession(key: string): string | null {
    this.logger.debug('StorageService', 'Getting session storage value', { key });
    try {
      return sessionStorage.getItem(key);
    } catch (error) {
      this.logger.error('StorageService', 'Failed to get session storage value', error);
      return null;
    }
  }
  
  /**
   * Removes a value from session storage.
   * @param key The key of the value to remove.
   */
  removeSession(key: string): void {
    this.logger.debug('StorageService', 'Removing session storage value', { key });
    try {
      sessionStorage.removeItem(key);
    } catch (error) {
      this.logger.error('StorageService', 'Failed to remove session storage value', error);
      throw error;
    }
  }
}